import { Component, Renderer2, inject } from '@angular/core';
import { Direction } from '@angular/cdk/bidi';
import { Title } from '@angular/platform-browser';
import { Account, AccountService } from '@nano/account/common';
import { L10nTranslationService } from 'angular-l10n';
import { Observable, combineLatest } from 'rxjs';
import { tap, map, filter } from 'rxjs/operators';
import { AuthService, AuthStep, UserProfile } from '../auth';
import { IdleService } from '@nano/core/idle';
import { L10nService } from '@nano/core/l10n';
import { CONFIG } from '@nano/core/config';
import { SseService } from '../sse';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-layout',
  templateUrl: 'layout.component.html',
  styleUrls: ['layout.component.less'],
  standalone: false
})
export class LayoutComponent {
  readonly accounts$: Observable<Account[]>;
  readonly authStep$: Observable<AuthStep>;
  readonly userProfile$: Observable<UserProfile>;

  isHeaderFooterVisible = false;
  direction: Direction;

  private bodyBackgroundColorUnauthorized: string;
  private bodyBackgroundColorAuthorized: string;
  private previouslyIsAuthenticated: boolean | undefined;

  private config = inject(CONFIG);

  constructor(
    private renderer: Renderer2,
    private accountService: AccountService,
    private authService: AuthService,
    private idleService: IdleService,
    private l10nService: L10nService,
    private titleService: Title,
    private translationService: L10nTranslationService,
    private sseService: SseService
  ) {
    this.accounts$ = this.accountService.accounts$;

    this.authStep$ = combineLatest([this.authService.authStep$, this.l10nService.schema$.pipe(filter((schema) => !!schema))]).pipe(
      map(([authStep, schema]) => {
        this.direction = schema.dir;
        this.titleService.setTitle(this.translationService.translate('title', { bankName: this.config.bankName }) as string);
        this.bodyBackgroundColorUnauthorized = this.config.bodyBackgroundColorUnauthorized;
        this.bodyBackgroundColorAuthorized = this.config.bodyBackgroundColorAuthorized;
        this.setBodyBackgroundColor(this.bodyBackgroundColorUnauthorized);
        this.isHeaderFooterVisible = (!this.config.oauthEnabled && !authStep.isAuthorized) || (this.config.oauthEnabled && authStep.isAuthenticated);
        this.setUserIdleWatching(authStep?.isAuthenticated);
        return authStep;
      })
    );

    this.userProfile$ = this.authService.userProfile$.pipe(
      tap((userProfile: UserProfile) => this.setBodyBackgroundColor(userProfile ? this.bodyBackgroundColorAuthorized : this.bodyBackgroundColorUnauthorized))
    );

    this.sseService.ibProductUpdated$.pipe(untilDestroyed(this)).subscribe(() => {
      window.location.reload();
    });
  }

  private setBodyBackgroundColor(color: string): void {
    if (color) {
      this.renderer.setStyle(document.body, 'background-color', color);
    }
  }

  private setUserIdleWatching(isAuthenticated?: boolean): void {
    if (this.previouslyIsAuthenticated !== isAuthenticated) {
      this.previouslyIsAuthenticated = isAuthenticated;
      if (isAuthenticated) {
        this.idleService.startWatching();
      } else {
        this.idleService.stopWatching();
      }
    }
  }
}
